import React, { useState, useEffect, useMemo } from 'react';
import {
  DialogContent, Table, TableBody, TableCell, TableContainer,
  TableRow, Paper, TableHead, Typography
} from '@mui/material';
import { useFetchUser } from '../../hooks/user-hooks';
import {
  useAddCustomerSiteMutation,
  useAddLANTopologyMutation,
  useAddWANTopologyMutation,
  useDeleteCustomerSiteMutation,
  useFetchCustomerSites,
  useFetchLANTopologies,
  useFetchWANTopologies,
  useUpdateCustomerSiteMutation,
} from '../../hooks/customer-hooks';
import { CustomerSite, LANTopology, WANTopology } from '../../types/customer';
import StbSimpleTable from '../security-toolbox-ui/StbSimpleTable';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { Controller, useForm } from 'react-hook-form';
import { HbrCheckbox, HbrInput, HbrOption, HbrSelect, HbrTextarea } from '@harbor/elements-react-standalone';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { renderErrorMessage } from '../security-toolbox-ui/security-toolbox-ui-utils';


type CustomerSitePayload = Omit<CustomerSite, 'lan_topology' | 'wan_topology'> & {
  lan_topology_id: number | null;
  wan_topology_id: number | null;
};

interface CustomerSitesFormProps {
  customerId: string;
}

const siteTypes = ['Campus', 'Data Center', 'Branch Office', 'Remote Site', 'Public Cloud'];
const lanArchitectures = ['Three Tier', 'Two Tier', 'Single Device'];
const accessLayers = ['Routed', 'Switched'];
const routingProtocols = ['OSPF', 'EIGRP', 'iBGP', 'ISIS', 'RIP', 'Static Routes'];
const connectivityOptions = ['MPLS', 'SD-WAN', 'Private Backbone', 'Other'];

const initialSite = {
  id: null,
  name: '',
  type: '',
  lan_topology: null,
  wan_topology: null,
  users: 0,
  notes: '',
  city: '',
  country: '',
};

const initialLanTopology = {
  architecture: '',
  access_layer: '',
  routing_protocol: '',
  qos_implemented: false,
  route_switch_multi_vendor: false,
  wireless_multi_vendor: false
};

const initialWanTopology = {
  mpls: null,
  sd_wan: null,
  type: '',
  private_backbone: null,
  direct_internet_access: false,
  qos_implemented: false,
  route_switch_multi_vendor: false
};

const CustomerSitesForm: React.FC<CustomerSitesFormProps> = ({ customerId }) => {
  const { data: user } = useFetchUser();

  const {
    control: siteControl,
    handleSubmit: siteHandleSubmit,
    reset: siteReset,
    formState: siteFormState,
    setValue: siteSetValue,
    watch: siteWatch,
  } = useForm<CustomerSite>({
    defaultValues: initialSite,
    mode: 'onChange',
  });
  const siteId = siteWatch('id');


  const {
    control: lanControl,
    handleSubmit: lanHandleSubmit,
    reset: lanReset,
    formState: lanFormState
  } = useForm<LANTopology>({
    defaultValues: initialLanTopology,
    mode: 'onChange',
  });

  const {
    control: wanControl,
    handleSubmit: wanHandleSubmit,
    reset: wanReset,
    formState: wanFormStateWan
  } = useForm<WANTopology>({
    defaultValues: initialWanTopology,
    mode: 'onChange',
  });

  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openSiteModal, setOpenSiteModal] = useState(false);

  // const [siteInDialog, setSiteInDialog] = useState<CustomerSite | null>(null);
  
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  
  const [openNewLanModal, setOpenNewLanModal] = useState(false);
  const [openNewWanModal, setOpenNewWanModal] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<number | null>(null);
  const [selectedSiteType, setSelectedSiteType] = useState<string | null>(null);

  // LAN/WAN topology modals
  const [createdWanTopology, setCreatedWanTopology] = useState<string | number | null>(null);
  const [createdLanTopology, setCreatedLanTopology] = useState<string | number | null>(null);

  // Fetches
  const { data: sitesData, isLoading, error } = useFetchCustomerSites({ customer: customerId }, { enabled: !!customerId });
  const sites = sitesData
    ? sitesData.map((site: CustomerSite) => ({
      ...site,
      lan_topology: site.lan_topology || null,
      wan_topology: site.wan_topology || null,
    }))
    : [];

  const { data: lanData } = useFetchLANTopologies({ customer: customerId }, { enabled: !!customerId });
  const { data: wanData } = useFetchWANTopologies({ customer: customerId }, { enabled: !!customerId });
  const lanTopologies = useMemo(() => lanData ?? [], [lanData]);
  const wanTopologies = useMemo(() => wanData ?? [], [wanData]);

  // Mutations
  const { mutateAsync: addCustomerSiteMutation } = useAddCustomerSiteMutation();
  const { mutateAsync: updateCustomerSiteMutation } = useUpdateCustomerSiteMutation();
  const { mutateAsync: deleteCustomerSiteMutation } = useDeleteCustomerSiteMutation();
  const { mutateAsync: addLANTopologyMutation } = useAddLANTopologyMutation();
  const { mutateAsync: addWANTopologyMutation } = useAddWANTopologyMutation();

  // -------- Site Modal Handlers --------
  const handleAddNewClick = () => {
    siteReset(initialSite);
    setOpenSiteModal(true);
  };

  const handleTagClick = (site: CustomerSite) => {
    console.log('Editing site:', site);
    siteReset(site);
    setOpenSiteModal(true);
  };

  const handleDeleteClick = (siteId: number) => {
    setSiteToDelete(siteId);
    setOpenDeleteModal(true);
  };

  // -------- Save/Update Site --------
  const handleSaveClick = async (data) => {
    const payload: CustomerSitePayload = {
      ...data,
      customer: customerId,
      last_updated: new Date(),
      updated_by: user ? user.email : 'system',
      lan_topology_id: data.lan_topology?.id || null,
      wan_topology_id: data.wan_topology?.id || null,
    };
    if (data.id) {
      await updateCustomerSiteMutation(payload);
    } else {
      await addCustomerSiteMutation(payload);
    }
  };

  // -------- Delete Site --------
  const confirmDelete = async () => {
    await deleteCustomerSiteMutation({ customer: customerId, id: siteToDelete });
    setSiteToDelete(null);
  };

  // -------- Topology Add/Edit --------
  const handleAddNewLanTopology = () => {
    lanReset(initialLanTopology);
    setOpenNewLanModal(true);
  };

  const handleAddNewWanTopology = () => {
    wanReset(initialWanTopology);
    setOpenNewWanModal(true);
  };

  // -------- Topology Save/Delete --------
  const handleSaveNewLanTopology = async (data) => {
    const newTopology = await addLANTopologyMutation({
      ...data,
      customer: customerId,
      last_updated: new Date(),
      updated_by: user ? user.email : 'system',
    });
    if (newTopology.id) {
      setCreatedLanTopology(newTopology.id);
    }
  }

  useEffect(() => {
    if (createdLanTopology && lanTopologies.length > 0) {
      const selectedLan = lanTopologies.find((lan) => lan.id === createdLanTopology) || null;
      if (selectedLan) {
        siteSetValue('lan_topology', selectedLan);
        setCreatedLanTopology(null);
      }
    }
  }, [createdLanTopology, lanTopologies, siteSetValue]);

  const handleSaveNewWanTopology = async (data) => {
    const newTopology = await addWANTopologyMutation({
      ...data,
      customer: customerId,
      last_updated: new Date(),
      updated_by: user ? user.email : 'system',
    });
    if (newTopology.id) {
      setCreatedWanTopology(newTopology.id);
    }
  };

  useEffect(() => {
    if (createdWanTopology && wanTopologies.length > 0) {
      const selectedWan = wanTopologies.find((wan) => wan.id === createdWanTopology) || null;
      if (selectedWan) {
        siteSetValue('wan_topology', selectedWan);
        setCreatedWanTopology(null);
      }
    }
  }, [createdWanTopology, wanTopologies, siteSetValue]);

  // -------- Site Type Table Logic --------
  const getSiteCounts = () => {
    const counts = siteTypes.reduce((acc, type) => ({ ...acc, [type]: 0 }), {});
    sites.forEach(site => {
      if (site.type in counts) {
        counts[site.type]++;
      }
    });
    return counts;
  };
  const siteCounts = getSiteCounts();
  const handleSiteTypeClick = (type: string) => {
    setSelectedSiteType(type);
    setOpenDetailsModal(true);
  };
  const filteredSites = sites.filter(site => site.type === selectedSiteType);

  // -------- Render --------
  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading customer sites</div>;
  return (
    <div>
      <div className="flex justify-between items-center">
        <h3>
          Sites
        </h3>
        <StbButton role="add" size="xsmall" onClick={handleAddNewClick} />
      </div>
      {/* Site Type Table */}
      <StbSimpleTable
        columns={['Type', 'Count']}
        data={siteTypes.reduce((acc, type) => {
          acc[type] = [type, siteCounts[type].toString()];
          return acc;
        }, {})}
        onRowClick={handleSiteTypeClick}
        noHeader={true}
      />

      {/* Details Modal */}
      <StbModal
        open={openDetailsModal}
        title={`Details for ${selectedSiteType}`}
        onClose={async () => setOpenDetailsModal(false)}
        size="x-large"
        role="information"
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>LAN Topology</TableCell>
                <TableCell>WAN Connectivity</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSites.length > 0 ? (
                filteredSites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell>{site.name}</TableCell>
                    <TableCell>{site.type}</TableCell>
                    <TableCell>{site.city}</TableCell>
                    <TableCell>{site.country}</TableCell>
                    <TableCell>
                      {site.lan_topology
                        ? `${site.lan_topology.architecture || ''} - ${site.lan_topology.access_layer || ''} - ${site.lan_topology.routing_protocol || ''}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {site.wan_topology
                        ? site.wan_topology.type || '-'
                        : '-'}
                    </TableCell>
                    <TableCell>{site.users}</TableCell>
                    <TableCell>{site.notes}</TableCell>
                    <TableCell className="flex flex-row">
                      <StbButton role="update" size="small" variant="text" iconOnly className="mr-2" onClick={() => handleTagClick(site)} />
                      <StbButton role="delete" size="small" variant="text" iconOnly onClick={() => handleDeleteClick(site.id)} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" align="center">No sites found for this type.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StbModal>

      {/* Site Modal */}
      <StbApiModal
        title={siteId ? 'Edit Site' : 'Add New Site'}
        open={openSiteModal}
        role={siteId ? "update" : "save"}
        onClose={async () => setOpenSiteModal(false)}
        onSubmit={siteHandleSubmit(handleSaveClick)}
        submitLabel={siteId ? "Update Site" : "Add Site"}
        submitDisabled={!siteFormState.isValid}
        submitSuccess="Site saved successfully"
        submitError="Error saving site"
        scrollable={true}
      >
        <Controller
          name="name"
          control={siteControl}
          rules={{
            validate: (value) => value && value.trim() !== '' ? true : 'Name is required'
          }}
          render={({ field }) => (
            <>
              <HbrInput
                value={field.value}
                error={!!siteFormState.errors['name']}
                onHbr-input={e => field.onChange(e.target.value)}
              >
                <div slot="label">Name <em className="text-gray-500">(required)</em></div>
                {renderErrorMessage(!!siteFormState.errors['name'], siteFormState.errors['name']?.message || '')}
              </HbrInput>
            </>
          )}
        />

        <Controller
          name="type"
          control={siteControl}
          rules={{
            validate: (value) => value ? true : 'Type is required'
          }}
          render={({ field }) => (
            <HbrSelect
              value={field.value}
              onHbr-input={e => field.onChange(e.target.value)}
            >
              {siteTypes.map((type) => (
                <HbrOption key={type} value={type}>
                  {type}
                </HbrOption>
              ))}
              <div slot="label">Type <em className="text-gray-500">(required)</em></div>
            </HbrSelect>
          )}
        />

        <Controller
          name="city"
          control={siteControl}
          render={({ field }) => (
            <HbrInput
              label="City"
              value={field.value}
              onHbr-change={e => field.onChange(e.target.value)}
            />
          )}
        />

        <Controller
          name="country"
          control={siteControl}
          render={({ field }) => (
            <HbrInput
              label="Country"
              value={field.value}
              onHbr-change={e => field.onChange(e.target.value)}
            />
          )}
        />

        <div className="flex flex-row items-center gap-2">
          <Controller
            name="lan_topology"
            control={siteControl}
            render={({ field }) => (
              <HbrSelect
                label="LAN Topology"
                value={String(field.value?.id ?? '')}
                onHbr-input={e => {
                  const lan = lanTopologies.find(l => l.id === Number(e.target.value)) || null;
                  field.onChange(lan);
                }}
                className="flex-1 w-full"
              >
                {lanTopologies.map((lan) => (
                  <HbrOption key={lan.id} value={lan.id.toString()}>
                    {`${lan.architecture || ''} - ${lan.access_layer || ''} - ${lan.routing_protocol || ''}`}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />
          <StbButton
            role="add"
            size="xsmall"
            variant="text"
            onClick={handleAddNewLanTopology}
            className="mt-3"
          />
        </div>

        <div className="flex flex-row items-center gap-2">
          <Controller
            name="wan_topology"
            control={siteControl}
            render={({ field }) => (
              <HbrSelect
                label="WAN Connectivity"
                value={String(field.value?.id ?? '')}
                onHbr-input={e => {
                  const wan = wanTopologies.find(w => w.id === Number(e.target.value)) || null;
                  field.onChange(wan);
                }}
                className="flex-1 w-full"
              >
                {wanTopologies.map((wan) => (
                  <HbrOption key={wan.id} value={wan.id.toString()}>
                    {wan.type || ''}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />
          <StbButton
            role="add"
            size="xsmall"
            variant="text"
            onClick={handleAddNewWanTopology}
            className="mt-3"
          />
        </div>

        <Controller
          name="users"
          control={siteControl}
          render={({ field }) => (
            <HbrInput
              label="Users"
              type="number"
              value={field.value == null ? '' : String(field.value)}
              onHbr-input={(e) => field.onChange(!e.target.value ? 0 : Number(e.target.value))}
            />
          )}
        />

        <Controller
          name="notes"
          control={siteControl}
          render={({ field }) => (
            <HbrTextarea
              label="Notes"
              value={field.value}
              onHbr-input={(e) => field.onChange(e.target.value)}
              rows={4}
            />
          )}
        />
      </StbApiModal>

      {/* Delete Confirmation Modal */}
      <StbApiModal
        open={openDeleteModal}
        role="delete"
        title="Confirm Delete"
        onSubmit={confirmDelete}
        submitSuccess="Site deleted successfully"
        submitError="Error deleting site"
        onClose={async () => setOpenDeleteModal(false)}
      >
        Are you sure you want to delete this site?
      </StbApiModal>

      {/* New LAN Topology Modal */}
      <StbApiModal
        open={openNewLanModal}
        title="Add New LAN Topology"
        role="save"
        onClose={async () => setOpenNewLanModal(false)}
        onSubmit={lanHandleSubmit(handleSaveNewLanTopology)}
        submitDisabled={!lanFormState.isValid}
        submitLabel="Add Topology"
        submitSuccess="LAN topology added successfully"
        submitError="Error adding LAN topology"
      >
        <form>
          <Controller
            name="architecture"
            control={lanControl}
            rules={{
              validate: (value) => value ? true : 'LAN Architecture is required'
            }}
            render={({ field }) => (
              <HbrSelect
                value={field.value}
                onHbr-input={e => field.onChange(e.target.value)}
              >
                {lanArchitectures.map((architecture) => (
                  <HbrOption key={architecture} value={architecture}>
                    {architecture}
                  </HbrOption>
                ))}
                <div slot="label">LAN Architecture <em className="text-gray-500">(required)</em></div>
              </HbrSelect>
            )}
          />
          
          <Controller
            name="access_layer"
            control={lanControl}
            render={({ field }) => (
              <HbrSelect
                label="Access Layer"
                value={field.value}
                onHbr-input={e => field.onChange(e.target.value)}
              >
                {accessLayers.map((accessLayer) => (
                  <HbrOption key={accessLayer} value={accessLayer}>
                    {accessLayer}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="routing_protocol"
            control={lanControl}
            render={({ field }) => (
              <HbrSelect
                label="Routing Protocol"
                value={field.value}
                onHbr-input={e => field.onChange(e.target.value)}
              >
                {routingProtocols.map((routingProtocol) => (
                  <HbrOption key={routingProtocol} value={routingProtocol}>
                    {routingProtocol}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
          />

          <Controller
            name="qos_implemented"
            control={lanControl}
            render={({ field }) => (
              <HbrCheckbox
                checked={field.value}
                onHbr-change={e => field.onChange(e.target.checked)}
                className="mt-4"
              >
                QoS Implemented
              </HbrCheckbox>
            )}
          />

          <Controller
            name="route_switch_multi_vendor"
            control={lanControl}
            render={({ field }) => (
              <HbrCheckbox
                checked={field.value}
                onHbr-change={e => field.onChange(e.target.checked)}
              >
                Route Switch Multi Vendor
              </HbrCheckbox>
            )}
          />

          <Controller
            name="wireless_multi_vendor"
            control={lanControl}
            render={({ field }) => (
              <HbrCheckbox
                checked={field.value}
                onHbr-change={e => field.onChange(e.target.checked)}
              >
                Wireless Multi Vendor
              </HbrCheckbox>
            )}
          />
        </form>
      </StbApiModal>

      {/* New WAN Topology Modal */}
      <StbApiModal
        open={openNewWanModal}
        title="Add New WAN Topology"
        role="save"
        onClose={async () => setOpenNewWanModal(false)}
        onSubmit={wanHandleSubmit(handleSaveNewWanTopology)}
        submitDisabled={!wanFormStateWan.isValid}
        submitLabel="Add Topology"
        submitSuccess="WAN topology added successfully"
        submitError="Error adding WAN topology"
      >
        <form>
          <Controller
            name="type"
            control={wanControl}
            rules={{
              validate: (value) => value ? true : 'Connectivity type is required'
            }}
            render={({ field }) => (
              <HbrSelect
                value={field.value}
                onHbr-input={e => field.onChange(e.target.value)}
              >
                {connectivityOptions.map((option) => (
                  <HbrOption key={option} value={option}>
                    {option}
                  </HbrOption>
                ))}
                <div slot="label">Connectivity Type <em className="text-gray-500">(required)</em></div>
              </HbrSelect>
            )}
          />

          <Controller
            name="direct_internet_access"
            control={wanControl}
            render={({ field }) => (
              <div className="mt-4">
                <HbrCheckbox
                  checked={field.value}
                  onHbr-change={e => field.onChange(e.target.checked)}
                >
                  Direct Internet Access
                </HbrCheckbox>
              </div>
            )}
          />

          <Controller
            name="qos_implemented"
            control={wanControl}
            render={({ field }) => (
              <div>
                <HbrCheckbox
                  checked={field.value}
                  onHbr-change={e => field.onChange(e.target.checked)}
                >
                  QoS Implemented
                </HbrCheckbox>
              </div>
            )}
          />

          <Controller
            name="route_switch_multi_vendor"
            control={wanControl}
            render={({ field }) => { 
              console.log('Rendering route_switch_multi_vendor with value:', field.value);
              return (
              <div>
                <HbrCheckbox
                  checked={field.value}
                  onHbr-change={e => {
                    console.log('Checkbox changed, new value:', e.target.checked);
                    return field.onChange(e.target.checked)}
                  }
                >
                  Route Switch Multi Vendor
                </HbrCheckbox>
              </div>
            )}}
          />
        </form>
      </StbApiModal>
    </div>
  );
};

export default CustomerSitesForm;